import { marked } from 'marked';

function isCellEmpty(cell) {
  if (!cell) return true;
  const text = (cell.textContent || '').replace(/\s/g, '');
  return text.length === 0;
}

export function hideEmptyTableColumns(html) {
  if (!html || typeof html !== 'string') return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const tables = doc.querySelectorAll('table');
  tables.forEach((table) => {
    const wrap = doc.createElement('div');
    wrap.className = 'ai-prose__table-wrap';
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);

    const rows = table.querySelectorAll('tr');
    const numCols = rows[0] ? rows[0].querySelectorAll('th, td').length : 0;
    if (numCols === 0) return;
    const emptyColIndices = [];
    for (let c = 0; c < numCols; c++) {
      let allDataRowsEmpty = true;
      for (let r = 1; r < rows.length; r++) {
        const cells = rows[r].querySelectorAll('th, td');
        const cell = cells[c];
        if (!isCellEmpty(cell)) {
          allDataRowsEmpty = false;
          break;
        }
      }
      if (allDataRowsEmpty) emptyColIndices.push(c + 1);
    }
    emptyColIndices.forEach((oneBased) => {
      table.classList.add('hide-col-' + oneBased);
    });
  });
  return doc.body.innerHTML;
}

const YOUTUBE_ID_RE = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;

function youTubeId(url) {
  const m = (url || '').match(YOUTUBE_ID_RE);
  return m ? m[1] : null;
}

/**
 * Turn YouTube links in the rendered HTML into a click-to-play video card
 * (thumbnail first frame + play button) that opens the video on YouTube in a
 * new tab — the embedded-video pattern used by Anthropic/OpenAI chats.
 */
export function embedYouTube(html) {
  if (!html || typeof html !== 'string') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href');
    const id = youTubeId(href);
    if (!id) return;
    const card = doc.createElement('a');
    card.className = 'ai-video';
    card.setAttribute('href', href);
    card.setAttribute('target', '_blank');
    card.setAttribute('rel', 'noopener noreferrer');
    card.setAttribute('aria-label', 'Play video on YouTube');
    card.innerHTML =
      `<img class="ai-video__thumb" src="https://img.youtube.com/vi/${id}/hqdefault.jpg" alt="Video thumbnail" loading="lazy" />`
      + '<span class="ai-video__play" aria-hidden="true">'
      + '<svg viewBox="0 0 68 48" width="54" height="38"><path class="ai-video__play-bg" d="M66.5 7.7a8 8 0 0 0-5.6-5.7C56 0.7 34 0.7 34 0.7s-22 0-26.9 1.3A8 8 0 0 0 1.5 7.7 83 83 0 0 0 0 24a83 83 0 0 0 1.5 16.3 8 8 0 0 0 5.6 5.7C12 47.3 34 47.3 34 47.3s22 0 26.9-1.3a8 8 0 0 0 5.6-5.7A83 83 0 0 0 68 24a83 83 0 0 0-1.5-16.3z"/><path d="M45 24 27 14v20z" fill="#fff"/></svg>'
      + '</span>';
    a.parentNode.replaceChild(card, a);
  });
  return doc.body.innerHTML;
}

// A GFM delimiter row: cells of dashes with optional alignment colons.
const TABLE_DELIM_RE = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

/** Split a markdown table row into cells (ignores escaped \| pipes). */
function tableCells(line) {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  return row.split(/(?<!\\)\|/);
}

/**
 * Repair markdown tables whose delimiter row doesn't match the header's column
 * count (e.g. 6 headers but `|---|---|---|---|---|`). GFM then refuses to
 * treat the block as a table and it renders as pipe-separated text. The model
 * makes this slip occasionally, so rebuild the delimiter row to the header's
 * width (keeping any alignment colons it did specify). Code fences are skipped.
 */
export function repairMarkdownTables(text) {
  if (!text || typeof text !== 'string' || text.indexOf('|') === -1) return text;
  const lines = text.split('\n');
  let inFence = false;
  for (let i = 0; i < lines.length - 1; i++) {
    if (/^\s*(```|~~~)/.test(lines[i])) {
      inFence = !inFence;
      continue;
    }
    if (inFence || lines[i].indexOf('|') === -1) continue;
    if (!TABLE_DELIM_RE.test(lines[i + 1])) continue;
    const headerCount = tableCells(lines[i]).length;
    const delimCells = tableCells(lines[i + 1]).map((c) => c.trim()).filter(Boolean);
    if (headerCount < 2 || delimCells.length === headerCount) continue;
    const fixed = [];
    for (let c = 0; c < headerCount; c++) fixed.push(delimCells[c] || '---');
    lines[i + 1] = `| ${fixed.join(' | ')} |`;
  }
  return lines.join('\n');
}

export function parseAssistantMarkdown(text) {
  if (!text) return '';
  return embedYouTube(hideEmptyTableColumns(marked.parse(repairMarkdownTables(text))));
}
